import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { SelectedTab } from 'features/navbar/types'

import { getWorkspaceTabPathname } from './getWorkspaceTabPathname'
import { isReusableDocumentsIndexTab } from './isReusableDocumentsIndexTab'
import {
  destinationResourceKey,
  STABLE_IDENTITY_DESTINATIONS,
} from './resourceTabs'

/**
 * Sidebar/destination reuse matching. Home never reuses (always a fresh
 * landing tab), Documents reuses only a pristine browse-all index,
 * stable-identity destinations match their one landing tab by resource key
 * (so reuse survives in-tab navigation, and resource tabs that merely BELONG
 * to the destination — e.g. subject detail tabs — are never reuse targets),
 * and every other destination keeps the legacy destination + pathname match.
 */
export function findReuseTargetTab(
  tabs: readonly DocumentTab[],
  options: { destinationKey: string; href: string }
): DocumentTab | undefined {
  if (
    options.destinationKey === SelectedTab.Home ||
    options.destinationKey === SelectedTab.DocumentsHome
  ) {
    return undefined
  }

  if (STABLE_IDENTITY_DESTINATIONS.has(options.destinationKey)) {
    const landingKey = destinationResourceKey(options.destinationKey)
    return tabs.find((tab) => tab.resourceKey === landingKey)
  }

  const requestedPathname = getWorkspaceTabPathname(options.href)

  return tabs.find((tab) => {
    if (options.destinationKey === SelectedTab.Documents) {
      return isReusableDocumentsIndexTab(tab)
    }

    // A tab with unsaved page edits is never a reuse target: the click gets
    // a fresh tab and the edits stay where they are.
    if (tab.dirty) return false

    const item = tab.history[tab.currentIndex]
    if (item.type !== 'destination') return false
    if (item.destinationKey !== options.destinationKey) return false
    return getWorkspaceTabPathname(item.href) === requestedPathname
  })
}
